import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Trash2, Plus, Minus, ShoppingBag, Truck, User, Tag, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { PageHero } from './BuyABook';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const Checkout = () => {
  const { items, removeItem, updateQty, total, hasPhysical, clearCart } = useCart();
  const { toast } = useToast();
  const [cust, setCust] = useState({ name: '', email: '', phone: '' });
  const [ship, setShip] = useState({ address: '', city: '', country: '' });
  const [placed, setPlaced] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState(null); // {code,label,discount,total}
  const [promoLoading, setPromoLoading] = useState(false);
  const orderIdRef = useRef(null);

  const grandTotal = promo ? promo.total : total;

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    try {
      const { data } = await axios.post(`${API}/promo/validate`, {
        code: promoInput.trim(),
        subtotal: total,
      });
      setPromo(data);
      toast({ title: 'Promo applied!', description: `${data.label} \u2014 you saved $${data.discount}` });
    } catch (err) {
      setPromo(null);
      toast({
        title: 'Invalid code',
        description: err.response?.data?.detail || 'That promo code is not valid.',
        variant: 'destructive',
      });
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromo = () => {
    setPromo(null);
    setPromoInput('');
  };

  const validate = () => {
    if (!cust.name || !cust.email) {
      toast({ title: 'Please enter your name and email.', variant: 'destructive' });
      return false;
    }
    if (hasPhysical && !ship.address) {
      toast({ title: 'Please provide a delivery address for physical copies.', variant: 'destructive' });
      return false;
    }
    return true;
  };

  const createOrder = async () => {
    if (!validate()) throw new Error('validation');
    const body = {
      items: items.map((i) => ({ book_id: i.id, format: i.format, qty: i.qty })),
      customer: { name: cust.name, email: cust.email, phone: cust.phone || null },
      shipping: hasPhysical
        ? { address: ship.address, city: ship.city, country: ship.country, phone: cust.phone || null }
        : null,
      promo_code: promo ? promo.code : null,
    };
    const { data } = await axios.post(`${API}/paypal/create-order`, body);
    orderIdRef.current = data.order_id;
    return data.paypal_order_id;
  };

  const onApprove = async (data) => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/paypal/capture-order`, {
        order_id: orderIdRef.current,
        paypal_order_id: data.orderID,
      });
      if (res.data.status === 'paid') {
        setPlaced(true);
        clearCart();
        toast({ title: 'Payment successful!', description: 'A confirmation email is on its way.' });
      }
    } catch (e) {
      toast({ title: 'Payment could not be completed.', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  if (placed) {
    return (
      <div className="fade-in">
        <PageHero title="Thank You" />
        <div className="max-w-xl mx-auto px-5 py-20 text-center">
          <div className="h-16 w-16 rounded-full bg-sand flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="h-8 w-8 text-brand-brass" />
          </div>
          <h2 className="font-serif text-3xl font-bold text-brand-ink">Your order has been placed</h2>
          <p className="text-brand-brown/80 mt-4">
            A confirmation has been sent to your email. For physical copies, our team begins the delivery
            process right away.
          </p>
          <Link to="/buy">
            <Button className="mt-8 bg-brand-ink hover:bg-brand-brown text-cream rounded-full h-12 px-8">
              Continue Browsing
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <PageHero title="Checkout" />

      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-16">
        {items.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingBag className="h-14 w-14 text-brand-goldlight mx-auto mb-5" />
            <h2 className="font-serif text-2xl font-bold text-brand-ink">Your cart is empty</h2>
            <p className="text-brand-brown/70 mt-2">Discover meaningful non-fiction to get started.</p>
            <Link to="/buy">
              <Button className="mt-6 bg-brand-gold hover:bg-brand-brass text-brand-ink hover:text-white rounded-full h-11 px-8">
                Browse Books
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-4">
              <h2 className="font-serif text-2xl font-bold text-brand-ink mb-2">Your Cart</h2>
              {items.map((i) => (
                <div key={i.key} className="flex gap-4 bg-white rounded-2xl p-4 shadow-sm">
                  <img src={i.image} alt={i.title} className="w-16 h-24 object-cover rounded-md" />
                  <div className="flex-1">
                    <h3 className="font-serif font-bold text-brand-ink">{i.title}</h3>
                    <p className="text-xs text-brand-brass italic">{i.subtitle}</p>
                    <span className="inline-block mt-1 text-[11px] uppercase tracking-wide bg-sand text-brand-brown px-2 py-0.5 rounded-full">
                      {i.format}
                    </span>
                    <div className="flex items-center gap-3 mt-3">
                      <div className="flex items-center border border-brand-goldlight rounded-full">
                        <button onClick={() => updateQty(i.key, i.qty - 1)} className="p-1.5 text-brand-brown">
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="px-2 text-sm text-brand-ink w-6 text-center">{i.qty}</span>
                        <button onClick={() => updateQty(i.key, i.qty + 1)} className="p-1.5 text-brand-brown">
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <button onClick={() => removeItem(i.key)} className="text-brand-brown/60 hover:text-destructive transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="font-serif font-bold text-brand-ink">${i.price * i.qty}</div>
                </div>
              ))}

              {/* Customer details */}
              <div className="bg-white rounded-2xl p-6 shadow-sm mt-6">
                <div className="flex items-center gap-2 mb-4">
                  <User className="h-5 w-5 text-brand-brass" />
                  <h3 className="font-serif text-lg font-bold text-brand-ink">Your Details</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-brand-ink">Full Name *</Label>
                    <Input value={cust.name} onChange={(e) => setCust({ ...cust, name: e.target.value })} className="mt-1.5 bg-cream border-brand-goldlight" />
                  </div>
                  <div>
                    <Label className="text-brand-ink">Email *</Label>
                    <Input type="email" value={cust.email} onChange={(e) => setCust({ ...cust, email: e.target.value })} className="mt-1.5 bg-cream border-brand-goldlight" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-brand-ink">Phone</Label>
                    <Input value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} className="mt-1.5 bg-cream border-brand-goldlight" />
                  </div>
                </div>
              </div>

              {hasPhysical && (
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <Truck className="h-5 w-5 text-brand-brass" />
                    <h3 className="font-serif text-lg font-bold text-brand-ink">Delivery Address</h3>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <Label className="text-brand-ink">Address *</Label>
                      <Textarea value={ship.address} onChange={(e) => setShip({ ...ship, address: e.target.value })} className="mt-1.5 bg-cream border-brand-goldlight" rows={2} />
                    </div>
                    <div>
                      <Label className="text-brand-ink">City</Label>
                      <Input value={ship.city} onChange={(e) => setShip({ ...ship, city: e.target.value })} className="mt-1.5 bg-cream border-brand-goldlight" />
                    </div>
                    <div>
                      <Label className="text-brand-ink">Country</Label>
                      <Input value={ship.country} onChange={(e) => setShip({ ...ship, country: e.target.value })} className="mt-1.5 bg-cream border-brand-goldlight" />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Summary */}
            <div>
              <div className="bg-white rounded-2xl p-6 shadow-md sticky top-32">
                <h3 className="font-serif text-xl font-bold text-brand-ink mb-4">Order Summary</h3>

                {/* Promo code */}
                <div className="mb-4">
                  {promo ? (
                    <div className="flex items-center justify-between bg-sand rounded-xl px-4 py-2.5">
                      <div className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-brand-gold" />
                        <span className="font-semibold text-brand-ink">{promo.code}</span>
                        <span className="text-brand-brown/70">applied</span>
                      </div>
                      <button onClick={removePromo} className="text-brand-brown/60 hover:text-destructive transition-colors">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Tag className="h-4 w-4 text-brand-brass absolute left-3 top-1/2 -translate-y-1/2" />
                        <Input
                          value={promoInput}
                          onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                          placeholder="Promo code"
                          className="bg-cream border-brand-goldlight pl-9 h-10 rounded-full uppercase"
                        />
                      </div>
                      <Button
                        onClick={applyPromo}
                        disabled={promoLoading}
                        variant="outline"
                        className="border-brand-gold text-brand-ink hover:bg-brand-gold hover:text-white rounded-full h-10 px-5 shrink-0"
                      >
                        {promoLoading ? '...' : 'Apply'}
                      </Button>
                    </div>
                  )}
                  <p className="text-[11px] text-brand-brown/50 mt-1.5">Try WELCOME10 or READ5</p>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-brand-brown/80">
                    <span>Subtotal</span>
                    <span>${total}</span>
                  </div>
                  {promo && (
                    <div className="flex justify-between text-brand-brass font-medium">
                      <span>Discount ({promo.code})</span>
                      <span>-${promo.discount}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-brand-brown/80">
                    <span>{hasPhysical ? 'Shipping' : 'Delivery'}</span>
                    <span>{hasPhysical ? 'At fulfilment' : 'Free (digital)'}</span>
                  </div>
                  <div className="border-t border-brand-goldlight/60 my-3" />
                  <div className="flex justify-between font-serif text-lg font-bold text-brand-ink">
                    <span>Total</span>
                    <span>${grandTotal}</span>
                  </div>
                </div>

                <div className="mt-6">
                  {PAYPAL_CLIENT_ID ? (
                    <PayPalScriptProvider options={{ 'client-id': PAYPAL_CLIENT_ID, currency: 'USD' }}>
                      <PayPalButtons
                        style={{ layout: 'vertical', color: 'gold', shape: 'pill', label: 'paypal' }}
                        disabled={processing}
                        createOrder={createOrder}
                        onApprove={onApprove}
                        onError={() =>
                          toast({ title: 'PayPal error', description: 'Something went wrong. Please retry.', variant: 'destructive' })
                        }
                      />
                    </PayPalScriptProvider>
                  ) : (
                    <p className="text-sm text-destructive">PayPal is not configured.</p>
                  )}
                </div>
                <p className="text-[11px] text-brand-brown/60 text-center mt-3">
                  Secure checkout via PayPal. A confirmation email is sent once payment completes.
                </p>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default Checkout;
